import { useEffect, useState, useRef, useMemo } from 'react';
import { fetchWithCorpId } from '../utils/api';
import useServerTable from '../utils/useServerTable';
import useColumnPreferences, { useHiddenFilterReset } from '../utils/useColumnPreferences';
import * as XLSX from 'xlsx';
import Drawer from './Drawer';
import InvoiceForm from './InvoiceForm';
import PageHeader from './ui/PageHeader';
import TableState from './ui/TableState';
import ColumnPicker from './ui/ColumnPicker';
import { ColumnCells, ColumnFilterRow, ColumnHeaderRow, StatusBadge } from './ui/TableBits';
import { formatDate } from '../utils/dateUtils';
import { useToast } from './ui/Toast';
import { useConfirm } from './ui/ConfirmDialog';

// One key per filterable column (issue #144); each maps to a same-named
// query param on GET /invoices (backend #131).
const EMPTY_FILTER = { number: '', customer: '', status: '' };

function InvoiceTable() {
  const toast = useToast();
  const confirm = useConfirm();
  const {
    rows: invoices, loading, error,
    filter, setFilter, handleFilterChange, clearFilters, hasActiveFilters,
    sortConfig, handleSort,
    page, setPage, size, handleChangePageSize,
    totalElements, totalPages, reload, fetchAll,
  } = useServerTable('/invoices', { emptyFilter: EMPTY_FILTER, initialSort: { key: 'id', direction: 'asc' } });
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const menuRefs = useRef({});
  const [statusOptions, setStatusOptions] = useState([]);

  // Drawer state
  const [drawer, setDrawer] = useState(null);
  const formRef = useRef(null);
  const openCreate = () => setDrawer({ mode: 'create' });
  const openEdit = (inv) => setDrawer({ mode: 'edit', id: inv.id });
  const closeDrawer = () => setDrawer(null);
  const handleDrawerSuccess = () => { closeDrawer(); reload(); };

  const handleDownloadPDF = async (id) => {
    try {
      const res = await fetchWithCorpId(`/invoices/${id}/pdf-db`, { method: 'GET', headers: { Accept: 'application/pdf' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      if (blob.type !== 'application/pdf') throw new Error('Invalid response type');
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `invoice-${id}.pdf`;
      document.body.appendChild(a); a.click(); window.URL.revokeObjectURL(url); document.body.removeChild(a);
    } catch (e) { toast.error(e.message ? `Failed to download PDF: ${e.message}` : 'Failed to download PDF'); }
  };

  const handleViewHTML = async (id) => {
    try {
      const res = await fetchWithCorpId(`/invoices/${id}/html-view`, { method: 'GET', headers: { Accept: 'text/html' } });
      const w = window.open('', '_blank'); w.document.write(res); w.document.close();
    } catch (e) { toast.error(e.message ? `Failed to view: ${e.message}` : 'Failed to view invoice'); }
  };

  const handleSendReminder = async (id, alreadySent) => {
    try {
      await fetchWithCorpId(`/invoices/${id}/remind`, { method: 'PUT' });
      reload();
      toast.success(alreadySent ? 'Reminder sent' : 'Invoice sent');
    } catch (e) {
      toast.error(e.message || (alreadySent ? 'Failed to send reminder' : 'Failed to send invoice'));
    }
  };

  const handleMarkAsPaid = async (id) => {
    try { await fetchWithCorpId(`/invoices/${id}/paid`, { method: 'PUT' }); reload(); toast.success('Invoice marked as paid'); }
    catch (e) { toast.error(e.message || 'Failed to mark as paid'); }
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: 'Delete invoice',
      message: 'This permanently deletes the invoice.',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      const res = await fetchWithCorpId(`/invoices/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete');
      reload();
      toast.success('Invoice deleted');
    } catch (e) { toast.error(e.message || 'Failed to delete invoice'); }
  };

  useEffect(() => {
    const close = (e) => {
      if (openActionMenu) { const ref = menuRefs.current[openActionMenu]; if (ref && !ref.contains(e.target)) setOpenActionMenu(null); }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [openActionMenu]);

  // Accumulate the set of statuses seen across fetches, mirroring the previous
  // in-fetch derivation now that the fetch itself lives in the shared hook.
  useEffect(() => {
    setStatusOptions(prev => {
      const set = new Set(prev);
      invoices.forEach(inv => { if (inv?.status) set.add(inv.status); });
      return Array.from(set).sort();
    });
  }, [invoices]);

  const handleDownloadExcel = async () => {
    try {
      const rows = await fetchAll();
      const ws = XLSX.utils.json_to_sheet(rows.map(inv => ({
        Number: inv.number, Customer: inv.customer, Amount: inv.amount,
        'Invoice Date': inv.invoiceDate, 'Due Date': inv.dueDate, Status: inv.status,
      })));
      const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, 'Invoices');
      XLSX.writeFile(wb, 'invoices.xlsx');
    } catch (e) {
      toast.error(e.message || 'Export failed');
    }
  };

  // Column catalogue in default order (issue #148). Number is locked because it
  // is the row's open link and the invoice's identity; Actions is locked
  // everywhere.
  const columnCatalogue = useMemo(() => [
    {
      key: 'number', label: 'Number', locked: true,
      filter: { name: 'number' },
      cell: inv => (
        <td>
          <button type="button" className="btn-link table-link" onClick={() => openEdit(inv)} title="Open invoice">
            {inv.number}
          </button>
        </td>
      ),
    },
    {
      key: 'customer', label: 'Customer',
      filter: { name: 'customer' },
      cell: inv => <td style={{ fontWeight: '500' }}>{inv.customer}</td>,
    },
    {
      key: 'amount', label: 'Amount',
      cell: inv => <td style={{ fontWeight: '600', color: 'var(--ink)' }}>${inv.amount?.toFixed(2)}</td>,
    },
    {
      key: 'invoiceDate', label: 'Invoice Date',
      cell: inv => <td style={{ color: 'var(--muted)', fontSize: '13px' }}>{formatDate(inv.invoiceDate)}</td>,
    },
    {
      key: 'dueDate', label: 'Due Date',
      cell: inv => <td style={{ color: 'var(--muted)', fontSize: '13px' }}>{formatDate(inv.dueDate)}</td>,
    },
    {
      key: 'status', label: 'Status',
      filter: { name: 'status', options: statusOptions },
      cell: inv => <td><StatusBadge status={inv.status} /></td>,
    },
    {
      key: 'actions', label: 'Actions', locked: true, sortable: false,
      cell: inv => (
        <td style={{ position: 'relative' }} ref={el => menuRefs.current[inv.id] = el}>
          <button
            onClick={() => setOpenActionMenu(openActionMenu === inv.id ? null : inv.id)}
            style={{ background: 'none', border: '1px solid var(--hairline)', borderRadius: '6px', padding: '5px 10px', cursor: 'pointer', color: 'var(--muted)', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px' }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="5" r="1"/><circle cx="12" cy="12" r="1"/><circle cx="12" cy="19" r="1"/></svg>
          </button>
          {openActionMenu === inv.id && (
            <div className="action-menu">
              <div className="action-menu-item" onClick={() => { setOpenActionMenu(null); handleViewHTML(inv.id); }}>View</div>
              <div className="action-menu-item" onClick={() => { setOpenActionMenu(null); handleDownloadPDF(inv.id); }}>Download PDF</div>
              {inv.status === 'Pending' && <>
                <div className="action-menu-item" onClick={() => { setOpenActionMenu(null); handleSendReminder(inv.id, inv.sent); }}>{inv.sent ? 'Remind' : 'Send'}</div>
                <div className="action-menu-item" onClick={() => { setOpenActionMenu(null); handleMarkAsPaid(inv.id); }}>Mark as Paid</div>
              </>}
              <div className="action-menu-item danger" onClick={() => { setOpenActionMenu(null); handleDelete(inv.id); }}>Delete</div>
            </div>
          )}
        </td>
      ),
    },
    // the open menu and the status list both change what a cell renders
  ], [openActionMenu, statusOptions]);

  const {
    columns, pickerColumns, toggle, move, step, reset, isDefault, saveError,
  } = useColumnPreferences('invoices', columnCatalogue);

  useEffect(() => { if (saveError) toast.error(saveError); }, [saveError, toast]);
  useHiddenFilterReset(columns, setFilter, EMPTY_FILTER);

  return (
    <div className="page-container">
      <PageHeader
        title="Invoices"
        subtitle="Track and manage all invoices"
        actions={
          <>
            <button className="btn-info" onClick={handleDownloadExcel}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: 6 }}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Export
            </button>
            <button className="btn-primary" onClick={openCreate}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: 6 }}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              New Invoice
            </button>
          </>
        }
      />

      <div className="filter-bar">
        <button className="btn-secondary" onClick={clearFilters}>Clear filters</button>
        <ColumnPicker
          columns={pickerColumns}
          onToggle={toggle}
          onMove={move}
          onStep={step}
          onReset={reset}
          isDefault={isDefault}
        />
      </div>

      <div className="table-wrapper">
        <table className="data-table" aria-busy={loading}>
          <thead>
            <ColumnHeaderRow columns={columns} sortConfig={sortConfig} onSort={handleSort} />
            <ColumnFilterRow columns={columns} filter={filter} onChange={handleFilterChange} />
          </thead>
          <tbody style={{ opacity: loading && invoices.length > 0 ? 0.55 : 1 }}>
            {invoices.length === 0 ? (
              <TableState
                colSpan={columns.length}
                loading={loading}
                error={error}
                onRetry={reload}
                filtered={hasActiveFilters}
                onClearFilters={clearFilters}
                emptyTitle="No invoices yet"
                emptyHint="Create an invoice or approve a timesheet to generate one."
                emptyAction={{ label: 'New Invoice', onClick: openCreate }}
              />
            ) : invoices.map(inv => (
              <tr key={inv.id}>
                <ColumnCells columns={columns} row={inv} />
              </tr>
            ))}
          </tbody>
        </table>
        <div className="pagination">
          <span style={{ fontSize: '13px', color: 'var(--muted)', marginRight: 'auto' }}>
            {typeof totalElements === 'number' ? `${totalElements} total` : ''}
          </span>
          <label style={{ fontSize: '13px', color: 'var(--muted)' }}>Rows:&nbsp;
            <select value={size} onChange={handleChangePageSize} style={{ fontSize: '13px', border: '1px solid var(--hairline)', borderRadius: '6px', padding: '3px 6px' }}>
              {[5, 10, 25, 50, 100].map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </label>
          <span style={{ fontSize: '13px', color: 'var(--muted)' }}>Page {totalPages > 0 ? page + 1 : 0} of {totalPages}</span>
          <button onClick={() => setPage(p => Math.max(p - 1, 0))} disabled={page === 0}>← Prev</button>
          <button onClick={() => setPage(p => Math.min(p + 1, totalPages - 1))} disabled={totalPages === 0 || page >= totalPages - 1}>Next →</button>
        </div>
      </div>

      <Drawer
        open={drawer !== null}
        onClose={closeDrawer}
        title={drawer?.mode === 'edit' ? 'Edit invoice' : 'New invoice'}
        subtitle={drawer?.mode === 'edit' ? 'Update invoice details and line items.' : 'Create a new invoice with line items.'}
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={closeDrawer}>Cancel</button>
            <button type="button" className="btn-primary" onClick={() => formRef.current?.requestSubmit()}>
              {drawer?.mode === 'edit' ? 'Save changes' : 'Create invoice'}
            </button>
          </>
        }
      >
        {drawer && (
          <InvoiceForm
            key={drawer.id || 'new'}
            mode={drawer.mode}
            invoiceId={drawer.id}
            formRef={formRef}
            hideActions
            onSuccess={handleDrawerSuccess}
          />
        )}
      </Drawer>
    </div>
  );
}

export default InvoiceTable;
